import { BrowserModule } from "@angular/platform-browser";
import { ErrorHandler, Injector, NgModule } from "@angular/core";
import { LocationStrategy, HashLocationStrategy } from "@angular/common";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import { AppComponent } from "./app.component";

// Import containers
import { DefaultLayoutComponent } from "./containers";

import { BackgroundDefaultLayoutComponent } from "./containers/background-default-layout/background-default-layout.component";

import { P404Component } from "./views/error/404.component";
import { P500Component } from "./views/error/500.component";
import { LoginComponent } from "./views/login/login.component";
import { RegisterComponent } from "./views/register/register.component";

import {
  AppAsideModule,
  AppBreadcrumbModule,
  AppHeaderModule,
  AppFooterModule,
  AppSidebarModule,
} from "@coreui/angular";

// Import routing module
import { AppRoutingModule } from "./app.routing";

// Import 3rd party components
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { TabsModule } from "ngx-bootstrap/tabs";
import { AppHeaderComponent } from "./containers/app-header/app-header.component";
import { AppFooterComponent } from "./containers/app-footer/app-footer.component";
import { BsDatepickerModule } from "ngx-bootstrap/datepicker";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { ApiUrl } from "./custom-services/api-url/api-url";
import { ApiUrlService } from "./custom-services/api-url/api-url.service";
import { ToastrModule } from "ngx-toastr";
import { ModelHelperService } from "./service/model-helper.service";
import { BasicInformationDTO } from "./model/dto/basic-information-dto";
import { DatePipe } from '@angular/common';
import { NgxUiLoaderModule, NgxUiLoaderConfig, SPINNER, POSITION, PB_DIRECTION } from "ngx-ui-loader";
import { PaymentSuccessComponent } from "./payment-success/payment-success.component";
import { AuthGuard } from "./auth.guard";
import { TokenInterceptorService } from "./service/token-interceptor.service";
import { NgxPrintModule } from "ngx-print";
import { LandingPageComponent } from './landing-page/landing-page.component';
import { JwtModule } from "@auth0/angular-jwt";
import { ELedgerLoginComponent } from './e-ledger-login/e-ledger-login.component';
import { QRCodeComponent } from "angularx-qrcode";
import { AChallanPaymentCheckComponent } from './a-challan-payment-check/a-challan-payment-check.component';
import { ETaxServiceContentComponent } from './e-tax-service-content/e-tax-service-content.component';
import { ETdsComponent } from './e-tds/e-tds.component';
import { ETaxPaymentComponent } from './e-tax-payment/e-tax-payment.component';
import { FAQComponent } from "./faq/faq.component";
import { UserManualComponent } from "./user-manual/user-manual.component";
import { ELedgerComponent } from './e-ledger/e-ledger.component';
import { setAppInjector } from "./app-injector";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import{PdfViewerModule} from "ng2-pdf-viewer";
import { TrpRedirectComponent } from './trp/trp-redirect/trp-redirect.component';
import { TrpLandingComponent } from './trp-landing/trp-landing.component'
import { SharedModule } from "./shared.module";
import { TaxCertificateManualComponent } from "./tax-certificate-manual/tax-certificate-manual.component";
import { FaqNewComponent } from "./faq-new/faq-new.component";
import { SentryErrorHandler } from "./sentry-error-handler";
import { LoaderInterceptorService } from "./service/loader-interceptor.service";
import{SpecialRegistrationManualComponent} from "./special-registration-manual/special-registration-manual.component";
import {RecaptchaInterceptor} from "./interceptor/recaptcha.interceptor";
import {environment} from "../environments/environment";
import {
  SpecialRegistrationEmailVerificationManualComponent
} from "./special-registration-email-verification-manual/special-registration-manual.component";
import { CommonReturnViewModule } from "./common-return-view/common-return-view.module";


export let AppInjector: Injector;

export function jwtTokenGetter() {
  return localStorage.getItem('token');
}

const ngxUiLoaderConfig: NgxUiLoaderConfig = {
  maxTime: 30000,
  minTime: 0
};

@NgModule({ declarations: [
        AppComponent,
        DefaultLayoutComponent,
        BackgroundDefaultLayoutComponent,
        P404Component,
        P500Component,
        LoginComponent,
        RegisterComponent,
        RegisterComponent,
        AppHeaderComponent,
        AppFooterComponent,
        PaymentSuccessComponent,
        LandingPageComponent,
        ELedgerLoginComponent,
        AChallanPaymentCheckComponent,
        ETaxServiceContentComponent,
        ETdsComponent,
        ETaxPaymentComponent,
        // GuidelineComponent,
        // ContactUsComponent,
        FAQComponent,
        UserManualComponent,
        ELedgerComponent,
        TrpRedirectComponent,
        TrpLandingComponent,
        TaxCertificateManualComponent,
        FaqNewComponent,
        SpecialRegistrationManualComponent,
        SpecialRegistrationEmailVerificationManualComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        AppAsideModule,
        AppBreadcrumbModule.forRoot(),
        AppFooterModule,
        AppHeaderModule,
        AppSidebarModule,
        BsDropdownModule.forRoot(),
        TabsModule.forRoot(),
        BsDatepickerModule.forRoot(),
        ToastrModule.forRoot(),
        NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
        NgxPrintModule,
        QRCodeComponent,
        ReactiveFormsModule,
        FormsModule,
        PdfViewerModule,
        SharedModule,
        CommonReturnViewModule,
        JwtModule.forRoot({
            config: {
                tokenGetter: jwtTokenGetter
            }
        })], providers: [
        {
            provide: LocationStrategy,
            useClass: HashLocationStrategy,
        },
        {
            provide: ApiUrl,
            useClass: ApiUrlService,
        },
        {
            provide: BasicInformationDTO,
            useClass: ModelHelperService,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptorService,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoaderInterceptorService,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RecaptchaInterceptor,
            multi: true
        },
        { provide: ErrorHandler, useClass: SentryErrorHandler },
        DatePipe, AuthGuard,
        provideHttpClient(withInterceptorsFromDi())
    ] })

export class AppModule {
  constructor(injector: Injector) {
    setAppInjector(injector);
}
}
